import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { fetchMarketingHubPage } from '../services/marketingHubService';
import {
    LANDING_SECTION_PATHS,
    MARKETING_HUB_LANDING_SLUG,
    MarketingHubPage as MarketingHubPageData,
    MarketingHubSection,
} from '../types/marketingHub';

function hasText(value: string | null | undefined): value is string {
    return Boolean(value && value.trim());
}

function SectionImages({ section }: { section: MarketingHubSection }) {
    if (!section.image1Url && !section.image2Url) {
        return null;
    }

    const alt = section.title || 'Marketing Hub image';

    return (
        <div className="marketing-hub-section-images">
            {section.image1Url && (
                <img src={section.image1Url} alt={alt} className="marketing-hub-section-image" />
            )}
            {section.image2Url && (
                <img
                    src={section.image2Url}
                    alt={alt}
                    className="marketing-hub-section-image"
                />
            )}
        </div>
    );
}

function SectionTitle({
    section,
    isLanding,
}: {
    section: MarketingHubSection;
    isLanding: boolean;
}) {
    if (!hasText(section.title)) {
        return null;
    }

    const landingHref = isLanding ? LANDING_SECTION_PATHS[section.index] : undefined;
    const title = `${section.index}. ${section.title}`;
    if (landingHref) {
        return (
            <h2 className="marketing-hub-section-title">
                <Link to={landingHref} className="marketing-hub-section-title-link">
                    {title}
                </Link>
            </h2>
        );
    }

    return <h2 className="marketing-hub-section-title">{title}</h2>;
}

function ExternalResourceLink({
    linkText,
    resourceUrl,
}: {
    linkText: string | null;
    resourceUrl: string | null;
}) {
    if (!hasText(linkText) || !hasText(resourceUrl)) {
        return null;
    }

    return (
        <p className="marketing-hub-section-link-wrap">
            <a
                href={resourceUrl}
                className="marketing-hub-section-link"
                target="_blank"
                rel="noopener noreferrer"
            >
                {linkText}
            </a>
        </p>
    );
}

function SectionResourceLink({ section, isLanding }: { section: MarketingHubSection; isLanding: boolean }) {
    if (isLanding) {
        return null;
    }

    return <ExternalResourceLink linkText={section.linkText} resourceUrl={section.resourceUrl} />;
}

export default function MarketingHubPage() {
    const { slug: slugParam } = useParams<{ slug?: string }>();
    const slug = slugParam || MARKETING_HUB_LANDING_SLUG;
    const isLanding = slug === MARKETING_HUB_LANDING_SLUG;

    const [page, setPage] = useState<MarketingHubPageData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadPage = async () => {
            setIsLoading(true);
            setNotFound(false);
            setLoadError(false);

            try {
                const result = await fetchMarketingHubPage(slug);
                if (!isMounted) {
                    return;
                }
                if (result === null) {
                    setPage(null);
                    setNotFound(true);
                } else {
                    setPage(result);
                }
            } catch (error) {
                console.error('Error loading Marketing Hub page:', error);
                if (!isMounted) {
                    return;
                }
                setPage(null);
                setLoadError(true);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        void loadPage();

        return () => {
            isMounted = false;
        };
    }, [slug]);

    const sanitizedIntroduction = useMemo(
        () => (page?.pageIntroduction ? DOMPurify.sanitize(page.pageIntroduction) : ''),
        [page?.pageIntroduction]
    );
    const sanitizedIntroContent = useMemo(
        () => (page?.introductionContent ? DOMPurify.sanitize(page.introductionContent) : ''),
        [page?.introductionContent]
    );
    const sanitizedSections = useMemo(
        () =>
            (page?.sections ?? []).map((section) => ({
                ...section,
                sanitizedContent: section.content ? DOMPurify.sanitize(section.content) : '',
            })),
        [page?.sections]
    );

    if (isLoading) {
        return <p className="marketing-hub-status" role="status">Loading page...</p>;
    }

    if (loadError) {
        return (
            <p className="marketing-hub-status marketing-hub-status-error">
                Unable to load this page right now. Please try again later.
            </p>
        );
    }

    if (notFound || !page) {
        return (
            <p className="marketing-hub-status">
                This Marketing Hub page could not be found.
            </p>
        );
    }

    const heading = page.pageHeading || page.title;

    return (
        <article className="marketing-hub-article">
            {heading && <h1 className="marketing-hub-heading">{heading}</h1>}
            {sanitizedIntroduction && (
                <div
                    className="marketing-hub-html marketing-hub-introduction"
                    dangerouslySetInnerHTML={{ __html: sanitizedIntroduction }}
                />
            )}
            <ExternalResourceLink
                linkText={page.introductionLinkText}
                resourceUrl={page.introductionResourceUrl}
            />
            {(hasText(page.introductionTitle) || sanitizedIntroContent) && (
                <section className="marketing-hub-intro-block">
                    {hasText(page.introductionTitle) && (
                        <h2 className="marketing-hub-intro-title">{page.introductionTitle}</h2>
                    )}
                    {sanitizedIntroContent && (
                        <div
                            className="marketing-hub-html"
                            dangerouslySetInnerHTML={{ __html: sanitizedIntroContent }}
                        />
                    )}
                </section>
            )}
            {sanitizedSections.map((section) => (
                <section
                    key={section.index}
                    className="marketing-hub-section"
                    aria-label={section.title || `Section ${section.index}`}
                >
                    <SectionTitle section={section} isLanding={isLanding} />
                    {section.sanitizedContent && (
                        <div
                            className="marketing-hub-html"
                            dangerouslySetInnerHTML={{ __html: section.sanitizedContent }}
                        />
                    )}
                    <SectionImages section={section} />
                    <SectionResourceLink section={section} isLanding={isLanding} />
                </section>
            ))}
        </article>
    );
}
